import React, { useState, useEffect } from 'react';
import axios from 'axios';
import clientCandidateDataService from '../../../services/clientCandidateDataService';

/**
 * CustomFieldsWidget
 * Tags (from firm_tags) and additional custom fields
 * 
 * custom_tags structure: { "tagId": { name: "TagName", color: "#hex" }, ... }
 */

const CustomFieldsWidget = ({ profile, candidateId, onRefresh }) => {
  const [activeForm, setActiveForm] = useState(null); // 'tag' or 'field'
  const [editingKey, setEditingKey] = useState(null);
  const [fieldForm, setFieldForm] = useState({ label: '', value: '' });
  
  // Firm tags state
  const [firmTags, setFirmTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');

  const clientData = profile?.client_data || {};
  
  // Parse custom_tags - should be { tagId: { name, color }, ... }
  const customTags = clientData.custom_tags || {};
  const customFields = clientData.custom_fields || {};

  // Load firm tags
  useEffect(() => {
    loadFirmTags();
  }, []);

  const loadFirmTags = async () => {
    try {
      setLoadingTags(true);
      const token = localStorage.getItem('authToken');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isInternal = user.user_type === 'admin' || user.user_type === 'internal';
      const endpoint = isInternal 
        ? '/api/candidate-archive/internal-archive/filter-options'
        : '/api/client/archive/filter-options';
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirmTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading firm tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const saveData = async (updates) => {
    try {
      await clientCandidateDataService.updateData(candidateId, { ...clientData, ...updates });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save');
    }
  };

  // Tag handlers
  const handleAddTag = (tag) => {
    const tagId = String(tag.id);
    if (customTags[tagId]) return; // Already has this tag
    
    const newTags = { 
      ...customTags, 
      [tagId]: { name: tag.name, color: tag.color } 
    };
    saveData({ custom_tags: newTags });
    setShowTagDropdown(false);
    setTagSearchQuery('');
  };

  const handleRemoveTag = async (tagId) => {
    const newTags = { ...customTags };
    delete newTags[tagId];
    await saveData({ custom_tags: newTags });
  };

  // Field handlers
  const handleAddField = () => { setFieldForm({ label: '', value: '' }); setActiveForm('field'); setEditingKey(null); };
  
  const handleEditField = (key) => { setFieldForm({ label: key, value: customFields[key] }); setEditingKey(key); setActiveForm('field'); };

  const handleSaveField = async () => {
    if (!fieldForm.label.trim()) { alert('Field name is required'); return; }
    const newFields = { ...customFields };
    if (editingKey && editingKey !== fieldForm.label) delete newFields[editingKey];
    newFields[fieldForm.label.trim()] = fieldForm.value.trim();
    await saveData({ custom_fields: newFields });
    setActiveForm(null); setEditingKey(null);
  };

  const handleDeleteField = async (key) => {
    if (!window.confirm(`Delete field "${key}"?`)) return;
    const newFields = { ...customFields };
    delete newFields[key];
    await saveData({ custom_fields: newFields });
  };

  const handleCancel = () => { setActiveForm(null); setEditingKey(null); };

  // Get tags that are not already assigned
  const availableTags = firmTags.filter(tag => !customTags[String(tag.id)]);
  
  // Filter by search
  const filteredAvailableTags = availableTags.filter(tag => 
    tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
  );

  const tagEntries = Object.entries(customTags);
  const fieldEntries = Object.entries(customFields);

  return (
    <div className="space-y-4">
      {/* Tags */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Tags</label>
          <div className="relative">
            <button 
              onClick={() => setShowTagDropdown(!showTagDropdown)} 
              className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1"
              disabled={availableTags.length === 0}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Tag
            </button>
            
            {showTagDropdown && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border rounded-lg shadow-lg z-50">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    value={tagSearchQuery}
                    onChange={(e) => setTagSearchQuery(e.target.value)}
                    placeholder="Search tags..."
                    className="w-full px-2 py-1 text-sm border rounded"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {loadingTags ? (
                    <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                  ) : filteredAvailableTags.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      {tagSearchQuery ? 'No matching tags' : 'No tags available'}
                    </div>
                  ) : (
                    filteredAvailableTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => handleAddTag(tag)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <span 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color || '#6B7280' }}
                        />
                        {tag.name}
                      </button>
                    ))
                  )}
                </div>
                {availableTags.length === 0 && !loadingTags && (
                  <div className="p-2 border-t text-xs text-gray-500 text-center">
                    All tags assigned or no firm tags created
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {tagEntries.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No tags added</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tagEntries.map(([tagId, tagData]) => {
              // Handle both old format (string) and new format (object with name/color).
              // When `tagData` is an object but missing `name`/`color` (corrupted or
              // partial write — e.g. from an extension save that didn't include the
              // name), fall back to looking up the canonical record in the firm
              // catalog (firmTags) before giving up. The previous fallback to the
              // raw tagId is what produced the "0 1 2 3 …" wall of numeric chips
              // when the per-candidate custom_tags JSON was missing names.
              const isOldFormat = typeof tagData === 'string';
              const catalogMatch = !isOldFormat
                ? firmTags.find(t => String(t.id) === String(tagId))
                : null;
              const tagName = isOldFormat
                ? tagId
                : (tagData?.name || catalogMatch?.name || `Tag #${tagId}`);
              const tagColor = isOldFormat
                ? '#6B7280'
                : (tagData?.color || catalogMatch?.color || '#6B7280');
              const tagValue = isOldFormat ? tagData : null;
              
              return (
                <div 
                  key={tagId} 
                  className="group inline-flex items-center gap-1 px-2 py-1 rounded-full"
                  style={{ backgroundColor: `${tagColor}20`, color: tagColor }}
                >
                  <span 
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tagColor }}
                  />
                  <span className="text-xs font-medium">{tagName}</span>
                  {isOldFormat && tagValue && (
                    <span className="text-xs opacity-70">: {tagValue}</span>
                  )}
                  <button 
                    onClick={() => handleRemoveTag(tagId)} 
                    className="ml-1 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/50 rounded transition-opacity"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className="border-gray-200" />

      {/* Custom Fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Custom Fields</label>
          {activeForm !== 'field' && (
            <button onClick={handleAddField} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Field
            </button>
          )}
        </div>

        {activeForm === 'field' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
            <h4 className="font-medium text-sm text-purple-900 mb-2">{editingKey ? 'Edit Field' : 'Add Custom Field'}</h4>
            <div className="space-y-2 mb-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Field Name *</label>
                <input type="text" value={fieldForm.label} onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })} className="w-full px-2 py-1 text-sm border rounded" placeholder="e.g., Notes" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Value</label>
                <textarea value={fieldForm.value} onChange={(e) => setFieldForm({ ...fieldForm, value: e.target.value })} rows={2} className="w-full px-2 py-1 text-sm border rounded" placeholder="Field value..." />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveField} className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">{editingKey ? 'Update' : 'Add'}</button>
              <button onClick={handleCancel} className="px-3 py-1 border text-xs rounded hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {fieldEntries.length === 0 && activeForm !== 'field' ? (
          <p className="text-sm text-gray-400 italic">No custom fields added</p>
        ) : (
          <div className="space-y-2">
            {fieldEntries.map(([key, value]) => (
              <div key={key} className="group bg-gray-50 rounded-lg p-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <label className="text-xs font-medium text-gray-600">{key}</label>
                    <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{value || <span className="text-gray-400 italic">No value</span>}</p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1 ml-2">
                    <button onClick={() => handleEditField(key)} className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDeleteField(key)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsWidget;
